/**
 * TASK-PBG-006: ParallaxBackgroundManager
 *
 * Orchestrator class for the 3-layer parallax background system.
 * Manages lifecycle, camera updates, and layer coordination.
 *
 * Layer Order (bottom to top):
 * 1. Far layer - Distant background (parallax factor 0.1)
 * 2. Mid layer - Industrial elements (parallax factor 0.3)
 * 3. Near layer - Floor texture (parallax factor 0.5)
 */

import * as PIXI from 'pixi.js';
import type { ParallaxBackgroundConfig, ParallaxLayerConfig, CameraState } from '../../types/terrain';
import { calculateBleedBounds, type BleedBounds } from './bleed-calculator';
import { createTilingSprite } from './tiling-sprite-factory';
import { createSingleSprite } from './single-sprite-factory';
import { updateParallaxPosition, updateParallaxZoom, initializeSpriteBasePosition } from './parallax-updater';
import { createColorTexture, createGradientTexture, createGridPatternTexture } from '../../utils/placeholder-textures';

/**
 * ParallaxBackgroundManager
 *
 * Manages the 3-layer parallax background system for the battlefield.
 * Creates TilingSprites for each layer and updates them based on camera movement.
 *
 * @example
 * ```typescript
 * const manager = new ParallaxBackgroundManager(config);
 * await manager.initialize(textures);
 *
 * // Add to scene
 * app.stage.addChildAt(manager.getContainer(), 0);
 *
 * // Update on camera move
 * manager.updateCameraPosition({ x: 100, y: 50, zoom: 1.5 });
 *
 * // Cleanup
 * manager.destroy();
 * ```
 */
export class ParallaxBackgroundManager {
  private container: PIXI.Container;
  private layers: {
    far: PIXI.TilingSprite | PIXI.Sprite | null;
    mid: PIXI.TilingSprite | PIXI.Sprite | null;
    near: PIXI.TilingSprite | PIXI.Sprite | null;
  };
  private config: ParallaxBackgroundConfig;
  private basePosition: { x: number; y: number };
  private initialized: boolean = false;

  /**
   * Create a new ParallaxBackgroundManager
   *
   * @param config - Parallax background configuration
   */
  constructor(config: ParallaxBackgroundConfig) {
    this.config = config;
    this.container = new PIXI.Container();
    this.layers = {
      far: null,
      mid: null,
      near: null,
    };
    this.basePosition = { x: 0, y: 0 };
  }

  /**
   * Initialize the parallax background system
   *
   * Creates all three layers with the provided textures or placeholders.
   * Adds layers to the container in the correct order (far, mid, near).
   *
   * @param textures - Optional map of texture URLs to PIXI.Texture instances
   *                   If not provided, placeholder textures will be generated
   */
  async initialize(textures?: Map<string, PIXI.Texture>): Promise<void> {
    if (this.initialized) {
      console.warn('ParallaxBackgroundManager already initialized');
      return;
    }

    // Calculate bleed bounds from grid bounds
    const bounds = calculateBleedBounds(
      this.config.gridBounds,
      this.config.bleedFactor
    );

    // Get or create textures for each layer
    const farTexture = this.getTextureOrPlaceholder(
      this.config.layers.far.textureUrl,
      textures,
      'far'
    );
    const midTexture = this.getTextureOrPlaceholder(
      this.config.layers.mid.textureUrl,
      textures,
      'mid'
    );
    const nearTexture = this.getTextureOrPlaceholder(
      this.config.layers.near.textureUrl,
      textures,
      'near'
    );

    // Create sprites for each layer (TilingSprite or Sprite based on config)
    this.layers.far = this.createLayerSprite(this.config.layers.far, farTexture, bounds);
    this.layers.mid = this.createLayerSprite(this.config.layers.mid, midTexture, bounds);
    this.layers.near = this.createLayerSprite(this.config.layers.near, nearTexture, bounds);

    // Initialize base positions for regular sprites (needed for parallax calculations)
    if (this.layers.far instanceof PIXI.Sprite && !(this.layers.far instanceof PIXI.TilingSprite)) {
      initializeSpriteBasePosition(this.layers.far);
    }
    if (this.layers.mid instanceof PIXI.Sprite && !(this.layers.mid instanceof PIXI.TilingSprite)) {
      initializeSpriteBasePosition(this.layers.mid);
    }
    if (this.layers.near instanceof PIXI.Sprite && !(this.layers.near instanceof PIXI.TilingSprite)) {
      initializeSpriteBasePosition(this.layers.near);
    }

    // Add layers to container in correct order (far to near)
    this.container.addChild(this.layers.far);
    this.container.addChild(this.layers.mid);
    this.container.addChild(this.layers.near);

    this.initialized = true;
  }

  /**
   * Update parallax layers based on camera position and zoom
   *
   * This should be called whenever the camera moves or zooms.
   * Updates both position (parallax offset) and scale (zoom) for all layers.
   *
   * @param camera - Current camera state
   *
   * @example
   * ```typescript
   * manager.updateCameraPosition({
   *   x: 500,
   *   y: 400,
   *   zoom: 1.5
   * });
   * ```
   */
  updateCameraPosition(camera: CameraState): void {
    if (!this.initialized || !this.layers.far || !this.layers.mid || !this.layers.near) {
      console.warn('ParallaxBackgroundManager not initialized');
      return;
    }

    // Update position for each layer
    updateParallaxPosition(this.layers.far, camera, this.config.layers.far, this.basePosition);
    updateParallaxPosition(this.layers.mid, camera, this.config.layers.mid, this.basePosition);
    updateParallaxPosition(this.layers.near, camera, this.config.layers.near, this.basePosition);

    // Update zoom for each layer
    updateParallaxZoom(this.layers.far, camera, this.config.layers.far);
    updateParallaxZoom(this.layers.mid, camera, this.config.layers.mid);
    updateParallaxZoom(this.layers.near, camera, this.config.layers.near);
  }

  /**
   * Get the container holding all parallax layers
   *
   * This should be added to the stage at the bottom of the layer stack.
   *
   * @returns PIXI.Container with all parallax layers
   *
   * @example
   * ```typescript
   * app.stage.addChildAt(manager.getContainer(), 0);
   * ```
   */
  getContainer(): PIXI.Container {
    return this.container;
  }

  /**
   * Get the near background sprite (dev tool: background position editor)
   */
  getNearSprite(): PIXI.Sprite | PIXI.TilingSprite | null {
    return this.layers.near;
  }

  /**
   * Clean up resources
   *
   * Destroys all sprites and the container. Should be called when
   * the battlefield is unloaded.
   */
  destroy(): void {
    if (this.container && !this.container.destroyed) {
      this.container.destroy({ children: true });
    }

    this.layers.far = null;
    this.layers.mid = null;
    this.layers.near = null;
    this.initialized = false;
  }

  /**
   * Create a sprite for a layer using the appropriate factory
   *
   * @param config - Layer configuration
   * @param texture - PIXI texture for the layer
   * @param bounds - Bleed bounds for the layer
   * @returns Either a TilingSprite or Sprite based on config
   */
  private createLayerSprite(
    config: ParallaxLayerConfig,
    texture: PIXI.Texture,
    bounds: BleedBounds
  ): PIXI.TilingSprite | PIXI.Sprite {
    // Default to tiling if not specified
    const useTiling = config.useTiling !== false;

    if (useTiling) {
      return createTilingSprite(config, texture, bounds);
    } else {
      return createSingleSprite(config, texture, bounds);
    }
  }

  /**
   * Get texture from map or generate placeholder
   *
   * @param textureUrl - URL key for the texture
   * @param textures - Optional texture map
   * @param layerType - Type of layer for placeholder generation
   * @returns PIXI.Texture
   */
  private getTextureOrPlaceholder(
    textureUrl: string,
    textures: Map<string, PIXI.Texture> | undefined,
    layerType: 'far' | 'mid' | 'near'
  ): PIXI.Texture {
    // Try to get texture from map
    if (textures && textures.has(textureUrl)) {
      return textures.get(textureUrl)!;
    }

    // Generate placeholder based on layer type
    switch (layerType) {
      case 'far':
        // Far layer: solid dark navy background
        return createColorTexture(0x0a1628, 128, 128);

      case 'mid':
        // Mid layer: subtle gradient
        return createGradientTexture(0x1a3a5c, 128, 128);

      case 'near':
        // Near layer: grid pattern floor
        return createGridPatternTexture(0x00d4ff, 128, 128);

      default:
        return PIXI.Texture.WHITE;
    }
  }
}
